import { Tag } from 'antd'
import { ERoles } from 'models/auth'
import { EMultiOrderStatus, EOrderHistoryEvent, EOrderStatus, ERecieveMethod } from 'models/orders'
import { TWithdrawalTransactionStatus } from 'models/transaction'
import { getWithdrawalTransactionStatusColor, getWithdrawalTransactionStatusLabel } from 'shared/constants/withdrawals'

interface IOrderStatusItemData {
  key: EOrderStatus,
  label: React.ReactNode
}

export const orderTags = {
  [EOrderStatus.ON_CONFIRMATION]: {
    value: EOrderStatus.ON_CONFIRMATION,
    label: 'На подтверждении',
    color: 'purple'
  },
  [EOrderStatus.CONFIRMED_BY_SELLER]: {
    value: EOrderStatus.CONFIRMED_BY_SELLER,
    label: 'Принят',
    color: 'orange'
  },
  [EOrderStatus.CANCELED]: {
    value: EOrderStatus.CANCELED,
    label: 'Отменён',
    color: 'red'
  },
  [EOrderStatus.ARCHIVED]: {
    value: EOrderStatus.ARCHIVED,
    label: 'Удален',
    color: 'red'
  },
  [EOrderStatus.IN_DELIVERY]: {
    value: EOrderStatus.IN_DELIVERY,
    label: 'В доставке',
    color: 'geekblue'
  },
  [EOrderStatus.WAITING_IN_POINT]: {
    value: EOrderStatus.WAITING_IN_POINT,
    label: 'В пункте выдачи',
    color: 'cyan'
  },
  [EOrderStatus.FINISHED]: {
    value: EOrderStatus.FINISHED,
    label: 'Завершён',
    color: 'green'
  },
  [EOrderStatus.NOT_PAID]: {
    value: EOrderStatus.NOT_PAID,
    label: 'Не оплачен',
    color: 'volcano'
  },
  [EOrderStatus.RETURNED_TO_SENDER]: {
    value: EOrderStatus.RETURNED_TO_SENDER,
    label: 'Едет обратно',
    color: 'default'
  },
  [EOrderStatus.AWAITING_RETURN]: {
    value: EOrderStatus.AWAITING_RETURN,
    label: 'Ожидает продавца',
    color: 'gold'
  },
  [EOrderStatus.RETURNED]: {
    value: EOrderStatus.RETURNED,
    label: 'Возвращён',
    color: 'red'
  }
}

// Этап продажи в истории заказа подписан так же, как её статус в бухгалтерии
const saleStageTag = (status: TWithdrawalTransactionStatus) => ({
  label: getWithdrawalTransactionStatusLabel(status),
  color: getWithdrawalTransactionStatusColor(status)
})

export const orderHistoryEventTags = {
  [EOrderHistoryEvent.PARTIAL_CANCEL]: {
    label: 'Частичная отмена',
    color: 'magenta'
  },
  [EOrderHistoryEvent.PENDING_7_DAYS]: saleStageTag('Ожидает 7 дней'),
  [EOrderHistoryEvent.READY_FOR_WITHDRAWAL]: saleStageTag('Готов к выводу'),
  [EOrderHistoryEvent.WITHDRAWAL_REQUESTED]: saleStageTag('Заявка на вывод'),
  [EOrderHistoryEvent.PENDING_WITHDRAWAL]: saleStageTag('Ожидает вывода'),
  [EOrderHistoryEvent.WITHDRAWN]: saleStageTag('Выведено')
}

// PENDING — супер-заказ создан, но касса ещё не подтвердила оплату
export const multiOrderTags = {
  [EMultiOrderStatus.PENDING]: {
    value: EMultiOrderStatus.PENDING,
    label: 'Не оплачен',
    color: 'volcano'
  },
  [EMultiOrderStatus.PAID]: {
    value: EMultiOrderStatus.PAID,
    label: 'Оплачен',
    color: 'green'
  },
  [EMultiOrderStatus.CANCELED]: {
    value: EMultiOrderStatus.CANCELED,
    label: 'Отменён',
    color: 'red'
  }
}

const canceledStatus: IOrderStatusItemData = {
  key: EOrderStatus.CANCELED,
  label: (
    <Tag color={orderTags[EOrderStatus.CANCELED].color}>
      {orderTags[EOrderStatus.CANCELED].label}
    </Tag>
  )
}

const confirmedStatus: IOrderStatusItemData = {
  key: EOrderStatus.CONFIRMED_BY_SELLER,
  label: (
    <Tag color={orderTags[EOrderStatus.CONFIRMED_BY_SELLER].color}>
      {orderTags[EOrderStatus.CONFIRMED_BY_SELLER].label}
    </Tag>
  )
}

const inDeliveryStatus: IOrderStatusItemData = {
  key: EOrderStatus.IN_DELIVERY,
  label: (
    <Tag color={orderTags[EOrderStatus.IN_DELIVERY].color}>
      {orderTags[EOrderStatus.IN_DELIVERY].label}
    </Tag>
  )
}

const waitingInPointStatus: IOrderStatusItemData = {
  key: EOrderStatus.WAITING_IN_POINT,
  label: (
    <Tag color={orderTags[EOrderStatus.WAITING_IN_POINT].color}>
      {orderTags[EOrderStatus.WAITING_IN_POINT].label}
    </Tag>
  )
}

const finishedStatus: IOrderStatusItemData = {
  key: EOrderStatus.FINISHED,
  label: (
    <Tag color={orderTags[EOrderStatus.FINISHED].color}>
      {orderTags[EOrderStatus.FINISHED].label}
    </Tag>
  )
}

const returnedToSenderStatus: IOrderStatusItemData = {
  key: EOrderStatus.RETURNED_TO_SENDER,
  label: (
    <Tag color={orderTags[EOrderStatus.RETURNED_TO_SENDER].color}>
      {orderTags[EOrderStatus.RETURNED_TO_SENDER].label}
    </Tag>
  )
}

const returnedStatus: IOrderStatusItemData = {
  key: EOrderStatus.RETURNED,
  label: (
    <Tag color={orderTags[EOrderStatus.RETURNED].color}>
      {orderTags[EOrderStatus.RETURNED].label}
    </Tag>
  )
}

const awaitingReturnStatus: IOrderStatusItemData = {
  key: EOrderStatus.AWAITING_RETURN,
  label: (
    <Tag color={orderTags[EOrderStatus.AWAITING_RETURN].color}>
      {orderTags[EOrderStatus.AWAITING_RETURN].label}
    </Tag>
  )
}

export const getOrderStatusItems = (status: EOrderStatus, role: ERoles, deliveryType: ERecieveMethod): IOrderStatusItemData[] => {
  if (status === EOrderStatus.ON_CONFIRMATION) {
    return [
      confirmedStatus,
      canceledStatus
    ]
  }

  if (status === EOrderStatus.CONFIRMED_BY_SELLER) {
    if (deliveryType === ERecieveMethod.PICKUP_POINT) {
      if (role === ERoles.ADMIN) {
        return [
          waitingInPointStatus,
          canceledStatus
        ]
      }
      return [waitingInPointStatus]
    }
    if (role === ERoles.ADMIN) {
      return [
        inDeliveryStatus,
        canceledStatus
      ]
    }
    // Свою доставку продавец ведёт сам, остальные статусы ставит перевозчик
    if (deliveryType === ERecieveMethod.OWN_DELIVERY) {
      return [inDeliveryStatus]
    }
    return []
  }

  if (status === EOrderStatus.IN_DELIVERY) {
    if (deliveryType === ERecieveMethod.COURIER || deliveryType === ERecieveMethod.OWN_DELIVERY) {
      if (role === ERoles.ADMIN) {
        return [
          finishedStatus,
          canceledStatus
        ]
      }
      return deliveryType === ERecieveMethod.OWN_DELIVERY ? [finishedStatus] : []
    }
    if (role === ERoles.ADMIN) {
      return [
        waitingInPointStatus,
        canceledStatus
      ]
    }
    return []
  }

  if (status === EOrderStatus.WAITING_IN_POINT) {
    if (role === ERoles.ADMIN) {
      return [
        finishedStatus,
        returnedToSenderStatus,
        canceledStatus
      ]
    }
    if (deliveryType === ERecieveMethod.PICKUP_POINT) {
      return [finishedStatus, returnedToSenderStatus]
    }
    return []
  }

  if (status === EOrderStatus.FINISHED) {
    if (role === ERoles.ADMIN) {
      // Из «Завершён» только «Едет обратно» → далее «Возвращён», отмена недоступна
      return [returnedToSenderStatus]
    }
    return []
  }

  if (status === EOrderStatus.RETURNED_TO_SENDER) {
    if (role === ERoles.ADMIN) {
      // Цепочка возврата строго по порядку: «Едет обратно» → «Ожидает продавца» → «Возвращён»
      return [awaitingReturnStatus]
    }

    if (deliveryType === ERecieveMethod.PICKUP_POINT) {
      return [
        waitingInPointStatus,
        finishedStatus
      ]
    }

    return [inDeliveryStatus]
  }

  if (status === EOrderStatus.AWAITING_RETURN) {
    if (role === ERoles.ADMIN) {
      return [returnedStatus]
    }
    return []
  }

  return []
}
